import React, { useMemo } from 'react';
import { Box, Text } from '@chakra-ui/react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Cell, ResponsiveContainer,
} from 'recharts';
import { factorColor, formatFactor } from './chartUtils';
import type { GlobalSummaryProject } from '~/common/api/zeiterfassungServerApi/ProjectRecordApi';
import useTranslate from '~/common/utils/translate';

interface Props {
  data: GlobalSummaryProject[];
}

const BAR_HEIGHT = 28;

export const AvgFactorByProjectChart: React.FC<Props> = ({ data }) => {
  const t = useTranslate();
  const sorted = useMemo(
    () => [...data]
      .filter((d) => d.avg_factor_calculated != null)
      .sort((a, b) => (b.avg_factor_calculated ?? 0) - (a.avg_factor_calculated ?? 0))
      .map((d) => ({ project: d.project_name, factor: d.avg_factor_calculated })),
    [data],
  );
  const chartHeight = Math.max(sorted.length * BAR_HEIGHT, 120);

  return (
    <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="lg" p={3}>
      <Text fontSize="sm" fontWeight="600" mb={2}>
        {t('projectRecords.chart.avgFactor')}
      </Text>
      <Box overflowY="auto" maxH="300px">
        <Box h={`${chartHeight}px`}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={sorted} layout="vertical">
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis dataKey="project" type="category" tick={{ fontSize: 11 }} width={100} />
              <Tooltip formatter={(value) => formatFactor(value as number)} />
              <Bar dataKey="factor" barSize={16}>
                {sorted.map((entry, i) => (
                  <Cell key={i} fill={factorColor(entry.factor)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Box>
  );
};
